import { SavingsBreakdown, SimulationWarning } from '../../../playground/endpoints/_shared';
import { simulateV2Product } from '../../api';
import { ApvSelectFormValues } from './schema';

export const SLUG = 'apv-select-insurance';

export interface ApvSelectCoverage {
  insured_capital: number;
  net_premium: number;
  tax_rate: number;
  gross_premium: number;
}

export interface ApvSelectPremiumBreakdown {
  basic: number;
  agreed: number;
  excess: number;
}

export interface ApvSelectYearlyRow {
  year: number;
  age: number;
  actuarial_age: number;
  accumulated_prime: number;
  coverage_costs: number;
  policy_expenses: number;
  policy_value: number;
  rentability: number;
  death_compensation: number;
  agreed_deposits_balance: number;
  vp_rescate: number;
  fiscal_bonus_balance: number;
  [key: string]: number | undefined;
}

/**
 * One reported fiscal component of `output/monthly.py::fiscal_breakdown`.
 * `eligible_amount` only ever accompanies the Bonificación Fiscal (Régimen
 * A); `deductible_amount` / `excess_amount` only the Régimen B deduction.
 */
export interface ApvSelectFiscalComponent {
  benefit: number;
  running_total: number;
  accumulated_interest: number;
  eligible_amount?: number;
  deductible_amount?: number;
  excess_amount?: number;
}

/**
 * The two regimes are mutually exclusive, so exactly one of
 * `bonificacion_fiscal` / `regime_b_deduction` is present on a given
 * response -- absent, not zeroed. The Agreed Deposit relief is always there:
 * employer money earns it under either regime.
 */
export interface ApvSelectFiscalBreakdown {
  agreed_deposit_relief: ApvSelectFiscalComponent;
  bonificacion_fiscal?: ApvSelectFiscalComponent;
  regime_b_deduction?: ApvSelectFiscalComponent;
}

export interface ApvSelectMonthlyRow {
  month: number;
  calendar_month: number;
  year: number;
  age: number;
  actuarial_age: number;
  policy_value: number;
  rentability: number;
  net_contribution: number;
  cost: number;
  accumulated_prime: number;
  coverage_costs: number;
  coverage_costs_breakdown?: Record<string, number>;
  policy_expenses: number;
  death_compensation: number;
  agreed_deposits_balance: number;
  vp_rescate: number;
  fiscal_bonus_balance: number;
  fiscal_breakdown?: ApvSelectFiscalBreakdown;
}

export interface ApvSelectResponse {
  uf_value: number;
  currency: string;
  timeframe_months: number;
  premium: ApvSelectPremiumBreakdown;
  coverages: Record<string, ApvSelectCoverage | undefined>;
  savings: SavingsBreakdown;
  projection_by_year: ApvSelectYearlyRow[];
  projection_by_month?: ApvSelectMonthlyRow[];
  warnings: SimulationWarning[];
}

/**
 * The rate fields as `ApvSelectInput` wants them, which is NOT what
 * `_shared`'s `buildRatePayload` produces:
 *
 * - `rate_mode` must travel. v2 defaults it to `fixed` and then demands the
 *   matching field (`product_validators.assert_rate_fields_present`), so a
 *   schedule sent without `rate_mode: 'tiered'` is rejected outright.
 * - v2's own name for the stepped mode is `tiered`; `stepped` is the form's
 *   label for the same thing.
 * - The schedule travels as a real JSON array. `_shared` stringifies it
 *   because v1 rides on query params; this route posts a JSON body, and
 *   `RateScheduleFromFlatJson` accepts the array directly.
 */
function buildRatePayload(values: ApvSelectFormValues) {
  return values.rate_mode === 'fixed'
    ? { rate_mode: 'fixed', projection_rate: values.projection_rate }
    : {
      rate_mode: 'tiered',
      projection_rate_schedule: values.projection_rate_schedule ?? [],
    };
}

export function buildApvSelectPayload(values: ApvSelectFormValues) {
  const {
    rate_mode: _rateMode,
    projection_rate: _projectionRate,
    projection_rate_schedule: _projectionRateSchedule,
    ...rest
  } = values;
  return {
    ...rest,
    ...buildRatePayload(values),
    // APV Select only supports compensation plan B
    // (`validators/common.assert_compensation_plan_b`), so there is no field
    // for it -- it is pinned here to keep the request self-describing.
    compensation_plan: 'B',
    // Asks the engine for `projection_by_month`, which the monthly table,
    // the monthly charts and every fiscal figure on this page read.
    debug: true,
  };
}

export async function simulateApvSelect(
  values: ApvSelectFormValues,
): Promise<ApvSelectResponse> {
  return simulateV2Product<ApvSelectResponse>(SLUG, buildApvSelectPayload(values));
}
